import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "../components/Logo";
import { ArrowRight, Loader2, Calendar } from "lucide-react";
import { toast } from "sonner";

const BOOK_DEMO = "https://link.quicklyster.ca/widget/booking/Uqx8qFGVds9HVLl42Bij";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const resetToken = searchParams.get("token") || "";

  useEffect(() => {
    if (location.pathname === "/auth/reset" || resetToken) {
      setMode("reset");
    }
  }, [location.pathname, resetToken]);

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setPassword("");
    setConfirmPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "forgot") {
      if (!email) { toast.error("Email is required"); return; }
    } else if (mode === "reset") {
      if (!resetToken) { toast.error("Reset link is missing or invalid"); return; }
      if (!password) { toast.error("New password is required"); return; }
      if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    } else if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success("If an account exists, reset instructions have been sent.");
        switchMode("login");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: resetToken, password });
        await refresh();
        toast.success("Password reset. Welcome back.");
        navigate("/dashboard");
      } else if (mode === "register") {
        await api.post("/auth/register", { email, password, name });
        await refresh();
        toast.success("Account created");
        navigate("/dashboard");
      } else {
        await api.post("/auth/login", { email, password });
        await refresh();
        toast.success("Welcome back");
        navigate("/dashboard");
      }
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || "Authentication failed";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[hsl(var(--retops-bg))]">
      {/* LEFT — navy brand panel */}
      <div className="hidden lg:flex bg-[hsl(var(--retops-navy))] text-white relative overflow-hidden p-12 flex-col justify-between">
        <Link to="/"><Logo gold /></Link>
        <div className="relative z-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--retops-gold))]">Revenue intelligence</p>
          <h2 className="font-instrument text-5xl leading-tight mt-4">Every cancelled customer left warning signs. RetOps catches them before they leave.</h2>
          <p className="text-white/70 mt-6 max-w-md leading-relaxed">Full QA coverage for human + AI support. Churn risk with dollars attached. Coaching on autopilot.</p>
        </div>
        <p className="text-xs text-white/40 relative z-10">© {new Date().getFullYear()} RetOps · Revenue Intelligence</p>
      </div>

      {/* RIGHT — auth card */}
      <div className="flex items-center justify-center p-8 md:p-14">
        <div className="w-full max-w-md" data-testid="auth-card">
          <div className="lg:hidden mb-8"><Link to="/"><Logo size="sm" /></Link></div>
          {mode !== "forgot" && mode !== "reset" && (
            <div className="inline-flex items-center bg-white border border-[hsl(var(--line))] rounded-full p-1 mb-6" data-testid="auth-mode-toggle">
              {["login", "register"].map((m) => (
                <button key={m} onClick={() => switchMode(m)} data-testid={`mode-${m}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-[hsl(var(--retops-navy))] text-white" : "text-[hsl(var(--ink-2))]"}`}>
                  {m === "login" ? "Sign in" : "Register"}
                </button>
              ))}
            </div>
          )}
          <h1 className="font-instrument text-4xl leading-tight text-[hsl(var(--retops-navy))]">
            {mode === "register" ? "Create your RetOps account." : mode === "forgot" ? "Reset your password." : mode === "reset" ? "Choose a new password." : "Welcome back."}
          </h1>
          <p className="text-[hsl(var(--ink-2))] mt-2 text-sm">
            {mode === "register"
              ? "Use your business email to keep team data separate."
              : mode === "forgot"
                ? "Enter your work email and we will send password reset instructions if the account exists."
                : mode === "reset"
                  ? "Enter a new password for your RetOps account."
                  : "Sign in with your business email and password."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-3))]">Your name</span>
                <br />
                <input data-testid="in-name" value={name} onChange={(e) => setName(e.target.value)} className="input mt-1.5 p-2  rounded-md w-full border-2 border-[hsl(var(--line))]" placeholder="Alex Rodriguez" />
              </label>
            )}
            {mode !== "reset" && (
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-3))]">Work email</span>
                <br />
                <input data-testid="in-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input mt-1.5 p-2  rounded-md w-full border-2 border-[hsl(var(--line))]" placeholder="you@business.com" required />
              </label>
            )}
            {mode !== "forgot" && (
              <>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-3))]">{mode === "reset" ? "New password" : "Password"}</span> <br />
                  <input data-testid="in-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input mt-1.5 p-2  rounded-md w-full border-2 border-[hsl(var(--line))]" placeholder={mode === "register" || mode === "reset" ? "min 8 characters" : "••••••••"} required />
                </label>
                {mode === "reset" && (
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ink-3))]">Confirm password</span> <br />
                    <input data-testid="in-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="input mt-1.5 p-2  rounded-md w-full border-2 border-[hsl(var(--line))]" placeholder="repeat new password" required />
                  </label>
                )}
              </>
            )}
            {/* {mode === "login" && (
              <button type="button" onClick={() => switchMode("forgot")} className="text-sm font-semibold text-[hsl(var(--retops-navy))] hover:text-[hsl(var(--retops-teal))]">
                Forgot password?
              </button>
            )} */}
            <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 py-3">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Please wait…" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : mode === "reset" ? "Reset password" : "Sign in"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {(mode === "forgot" || mode === "reset") && (
            <button type="button" onClick={() => switchMode("login")} className="mt-5 w-full text-sm font-semibold text-[hsl(var(--retops-navy))] hover:text-[hsl(var(--retops-teal))]">
              Back to sign in
            </button>
          )}

          <div className="mt-6 pt-6 border-t border-[hsl(var(--line))]">
            <a href={BOOK_DEMO} target="_blank" rel="noreferrer" data-testid="book-demo-link" className="flex items-center justify-center gap-2 text-sm font-semibold text-[hsl(var(--retops-navy))] hover:text-[hsl(var(--retops-teal))]">
              <Calendar className="w-4 h-4" /> Book a demo instead →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
